import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';
import publicAxios from '../../api/publicAxios';

// Fetch Clinics
export const fetchClinics = createAsyncThunk(
  'booking/fetchClinics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicAxios.get('clinics/');
      // Handle both array and paginated responses
      const clinics = Array.isArray(response.data) 
        ? response.data 
        : (Array.isArray(response.data?.results) ? response.data.results : []);
      return clinics;
    } catch (error) {
      console.error('Error fetching clinics:', error);
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to fetch clinics. Please try again.'
      );
    }
  }
);

// Fetch Doctors by Clinic (or all doctors if no clinicId provided)
export const fetchDoctors = createAsyncThunk(
  'booking/fetchDoctors',
  async (clinicId, { rejectWithValue }) => {
    try {
      // If clinicId is provided, fetch doctors for that clinic
      if (clinicId) {
        const response = await publicAxios.get(`doctors/clinic/${clinicId}/`);
        return Array.isArray(response.data.results) ? response.data.results : (Array.isArray(response.data) ? response.data : []);
      } else {
        // If no clinicId, fetch all available doctors for public booking
        const response = await publicAxios.get('doctors/list/', {
          params: {
            page: 1,
            page_size: 100 // Get a reasonable number of doctors
          }
        });
        return Array.isArray(response.data.results) ? response.data.results : [];
      }
    } catch (error) {
      console.error('Error fetching doctors for booking:', error);
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to fetch doctors. Please try again.'
      );
    }
  }
);

// Fetch Availability for a Doctor
export const fetchAvailability = createAsyncThunk(
  'booking/fetchAvailability',
  async ({ doctorId, date }, { rejectWithValue }) => {
    try {
      const response = await publicAxios.get(
        `doctors/${doctorId}/availability/`,
        { params: { date } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch availability'
      );
    }
  }
);

// Book Appointment
export const bookAppointment = createAsyncThunk(
  'booking/bookAppointment',
  async (data, { rejectWithValue }) => {
    try {
      const { doctorId, clinicId, scheduledDate, scheduledTime } = data;
      const response = await axiosInstance.post(`doctors/${doctorId}/book/`, {
        clinic_id: clinicId || null,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to book appointment'
      );
    }
  }
);

const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    clinics: [],
    doctors: [],
    availability: [],
    bookingSuccess: null,
    loading: false,
    error: null,
  },
  reducers: {
    resetBookingState: (state) => {
      state.bookingSuccess = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClinics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClinics.fulfilled, (state, action) => {
        state.loading = false;
        state.clinics = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(fetchClinics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchDoctors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.availability = action.payload || [];
        // state.slotDuration = action.payload.slot_duration || 30;
      })
      .addCase(fetchAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(bookAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bookAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.bookingSuccess = action.payload.message;
      })
      .addCase(bookAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetBookingState } = bookingSlice.actions;

export default bookingSlice.reducer;
